import { useConfirmDeleteModal } from '@/features/invoice_reports/components/ConfirmDeleteModal';
import { useConfirmCancelModal } from '@/features/invoice_reports/components/ConfirmCancelModal';
import { useConfirmDuplicateModal } from '@/features/invoice_reports/components/ConfirmDuplicateModal';
import { ShareButton } from '@/features/invoice_reports/components/ShareDrawer';
import { useDuplicateMenuItems } from '@/features/invoice_reports/components/hooks/useDuplicateMenuItems';
import type { ReportActionState } from '@/features/invoice_reports/types';
import { useAxisAuth2ndApiFeatureFlag } from '@/hooks/feature_flag/useAxisAuth2ndApiFeatureFlag';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ActionMenu,
  ButtonGroup,
  ButtonV2,
  IconCollapsed,
  LinkButton,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import styles from './HeaderButtons.module.scss';

const cx = classnames.bind(styles);

type HeaderButtonsProps = {
  reportId: string;
  detailPath?: string;
  editPath?: string;
  editAction?: ReportActionState;
  deleteAction?: ReportActionState;
  cancelAction?: ReportActionState;
  shareAction?: ReportActionState;
  viewersCount?: number | null;
  isCopyable?: boolean;
  onOpenShareViewers?: () => void;
  reportNumber?: string;
  reportTitle?: string;
  reportTotalAmount?: string | null;
  apPayeeName?: string | null;
};

export const HeaderButtons: FC<HeaderButtonsProps> = memo(
  ({
    reportId,
    detailPath,
    editPath,
    editAction,
    deleteAction,
    cancelAction,
    shareAction,
    viewersCount,
    isCopyable,
    onOpenShareViewers,
    reportNumber,
    reportTitle,
    reportTotalAmount,
    apPayeeName,
  }) => {
    const { t } = useTranslation();
    const { onOpenConfirmDuplicateModal } = useConfirmDuplicateModal();
    const { onOpenConfirmDeleteModal } = useConfirmDeleteModal();
    const { onOpenConfirmCancelModal } = useConfirmCancelModal();
    const container = useGlobalContainerRef();
    const isAxisAuth2ndApiFeatureEnabled = useAxisAuth2ndApiFeatureFlag();

    /**
     * 詳細 / 編集 navigate through a real `href` rather than
     * `window.location.href`, so the browser can offer its native
     * "Open link in new tab", Cmd/Ctrl+click and middle-click (NA-5376).
     *
     * `||` rather than `??`: an empty path must fall back too, otherwise the
     * anchor renders as `href=""` — a link that merely reloads the page while
     * advertising a new-tab affordance.
     */
    const detailHref = detailPath || `/invoice_reports/${reportId}`;

    const handleEditClick = useCallback(() => {
      if (editPath) window.location.href = editPath;
    }, [editPath]);

    const handleDuplicateClick = useCallback(
      (shareWithSameViewers?: boolean) => {
        onOpenConfirmDuplicateModal({
          id: reportId,
          number: reportNumber,
          title: reportTitle,
          apPayeeName: apPayeeName,
          totalAmount: reportTotalAmount,
          shareWithSameViewers,
        });
      },
      [
        reportId,
        onOpenConfirmDuplicateModal,
        reportNumber,
        reportTitle,
        apPayeeName,
        reportTotalAmount,
      ]
    );

    const handleDuplicateWithSameViewers = useCallback(
      () => handleDuplicateClick(true),
      [handleDuplicateClick]
    );
    const handleDuplicateWithoutSharing = useCallback(
      () => handleDuplicateClick(false),
      [handleDuplicateClick]
    );

    const duplicateGroupItems = useDuplicateMenuItems({
      isCopyable: !!isCopyable,
      isProcessing: false,
      canShare: isAxisAuth2ndApiFeatureEnabled && !!shareAction?.visible,
      hasViewers: !!viewersCount,
      onDuplicateWithSameViewers: handleDuplicateWithSameViewers,
      onDuplicateWithoutSharing: handleDuplicateWithoutSharing,
    });

    const duplicateDropdownItems = useMemo(
      () =>
        duplicateGroupItems?.map((item) => ({
          key: item.key,
          label: item.label,
          disabled: item.disabled,
          onClick: item.onClick,
        })),
      [duplicateGroupItems]
    );

    const duplicateButtonProps = useMemo(
      () => ({
        size: 'sm' as const,
        isTertiary: true,
        isSecondary: false,
        rightIcon: <IconCollapsed size={16} />,
        children: t('duplicate'),
      }),
      [t]
    );

    const handleDelete = useCallback(() => {
      if (!reportId) return;
      onOpenConfirmDeleteModal({
        id: reportId,
        number: reportNumber,
        title: reportTitle,
        apPayeeName,
        totalAmount: reportTotalAmount,
      });
    }, [
      reportId,
      reportNumber,
      reportTitle,
      apPayeeName,
      reportTotalAmount,
      onOpenConfirmDeleteModal,
    ]);

    const handleCancel = useCallback(() => {
      onOpenConfirmCancelModal({
        id: reportId,
        number: reportNumber,
        title: reportTitle,
        apPayeeName,
        totalAmount: reportTotalAmount,
      });
    }, [
      reportId,
      reportNumber,
      reportTitle,
      apPayeeName,
      reportTotalAmount,
      onOpenConfirmCancelModal,
    ]);

    const isEditDisabled = editAction !== undefined && !editAction.allowed;
    const isDeleteDisabled =
      deleteAction !== undefined && !deleteAction.allowed;
    const isCancelDisabled =
      cancelAction !== undefined && !cancelAction.allowed;
    return (
      <ButtonGroup>
        {cancelAction?.visible &&
          (isCancelDisabled && cancelAction.reason ? (
            <Tooltip
              title={cancelAction.reason}
              overlayClassName={cx('editTooltip')}
              getPopupContainer={container}
            >
              <span className={cx('dangerButton')}>
                <ButtonV2 size='sm' isTransparent color='danger' isDisabled>
                  {t('ap_report_status_canceled')}
                </ButtonV2>
              </span>
            </Tooltip>
          ) : (
            <span className={cx('dangerButton')}>
              <ButtonV2
                size='sm'
                isTransparent
                color='danger'
                isDisabled={isCancelDisabled}
                onClick={handleCancel}
              >
                {t('ap_report_status_canceled')}
              </ButtonV2>
            </span>
          ))}
        {deleteAction?.visible &&
          (isDeleteDisabled && deleteAction.reason ? (
            <Tooltip
              title={deleteAction.reason}
              overlayClassName={cx('editTooltip')}
              getPopupContainer={container}
            >
              <span className={cx('dangerButton')}>
                <ButtonV2 size='sm' isTransparent color='danger' isDisabled>
                  {t('delete')}
                </ButtonV2>
              </span>
            </Tooltip>
          ) : (
            <span className={cx('dangerButton')}>
              <ButtonV2
                size='sm'
                isTransparent
                color='danger'
                isDisabled={isDeleteDisabled}
                onClick={handleDelete}
              >
                {t('delete')}
              </ButtonV2>
            </span>
          ))}
        {isAxisAuth2ndApiFeatureEnabled && shareAction?.visible && (
          <ShareButton
            shareAction={shareAction}
            onOpenShareViewers={onOpenShareViewers}
          />
        )}
        {duplicateDropdownItems ? (
          <ActionMenu
            type='button'
            items={duplicateDropdownItems}
            getPopupContainer={container}
            buttonProps={duplicateButtonProps}
          />
        ) : (
          <ButtonV2
            size='sm'
            isTertiary
            isDisabled={!isCopyable}
            onClick={() => handleDuplicateClick(undefined)}
          >
            {t('duplicate')}
          </ButtonV2>
        )}
        <LinkButton
          size='sm'
          isTertiary
          // LinkButton defaults isSecondary to true, unlike ButtonV2 — keep 詳細 tertiary.
          isSecondary={false}
          href={detailHref}
        >
          {t('detail')}
        </LinkButton>
        {editAction?.visible &&
          /**
           * Branch on isEditDisabled ALONE and nest the reason check inside it.
           * Conditioning on `isEditDisabled && editAction.reason` would route a
           * legal `{ allowed: false, reason: null }` into the enabled arm,
           * exposing edit_path as a right-clickable link the user may not open.
           */
          (isEditDisabled ? (
            editAction.reason ? (
              <Tooltip
                title={editAction.reason}
                overlayClassName={cx('editTooltip')}
                getPopupContainer={container}
              >
                <ButtonV2 size='sm' isSecondary isDisabled>
                  {t('edit')}
                </ButtonV2>
              </Tooltip>
            ) : (
              <ButtonV2 size='sm' isSecondary isDisabled>
                {t('edit')}
              </ButtonV2>
            )
          ) : editPath ? (
            <LinkButton size='sm' isSecondary href={editPath}>
              {t('edit')}
            </LinkButton>
          ) : (
            /**
             * No usable path. `handleEditClick` already no-ops on an empty
             * path, so this arm avoids rendering `href=""`.
             */
            <ButtonV2 size='sm' isSecondary onClick={handleEditClick}>
              {t('edit')}
            </ButtonV2>
          ))}
      </ButtonGroup>
    );
  }
);

HeaderButtons.displayName = 'HeaderButtons';
